import * as React from "react";
import { Button } from "@/components/ui/button";

type Props = { children: React.ReactNode };
type State = { hasError: boolean };

// Top-level error boundary. Without one, any uncaught render error unmounts the
// React tree and leaves a dead, unscrollable shell behind — which on an iOS
// Home Screen (standalone) app is indistinguishable from a frozen tab, with no
// browser chrome to reload from. Here we catch it and offer a hard reload.
export default class RootErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(): State {
    return { hasError: true };
  }

  componentDidCatch(error: unknown, info: unknown) {
    // Surface in dev tools / remote-debugger; safe to no-op in production.
    console.error("Uncaught error in React tree:", error, info);
  }

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <main className="flex min-h-screen items-center justify-center bg-page p-6 text-ink-body">
        <div className="max-w-md text-center">
          <h1>Something went wrong</h1>
          <p className="mt-2">
            The app hit an unexpected error. Reloading usually fixes it.
          </p>
          <Button
            className="mt-6"
            onClick={() => window.location.reload()}
          >
            Reload
          </Button>
        </div>
      </main>
    );
  }
}
